// API service for payroll management
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
console.log("API_BASE is:", API_BASE);

// Token expiration check utility
const isTokenExpired = (token) => {
  // If no token, it's not expired - it just doesn't exist
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= expiry;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // If token is malformed, treat as expired
  }
};

// Auto-logout function
const handleTokenExpiration = () => {
  localStorage.removeItem("token");
  // Redirect to login page
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

class ApiService {
  constructor() {
    this.token = localStorage.getItem("token");
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem("token", token);
  }

  checkTokenExpiration() {
    if (isTokenExpired(this.token)) {
      console.log("Token expired, logging out...");
      handleTokenExpiration();
      return false;
    }
    return true;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("token");
  }

  async request(endpoint, options = {}) {
    // Only check token expiration for non-auth requests when we have a token
    const isLoginRequest = endpoint.includes("/api/auth/");

    if (!isLoginRequest && this.token) {
      // Check token expiration before making request
      if (!this.checkTokenExpiration()) {
        throw new Error(
          "Authentication token has expired. Please log in again.",
        );
      }
    }

    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    // Debug logging
    const fullUrl = `${API_BASE}${endpoint}`;
    console.log(`API Request: ${fullUrl}`, {
      method: options.method || "GET",
      headers: {
        ...headers,
        Authorization: headers.Authorization ? "Bearer [REDACTED]" : "None",
      },
      body: options.body ? JSON.parse(options.body) : undefined,
      API_BASE: API_BASE,
      endpoint: endpoint,
      fullUrl: fullUrl,
      tokenExists: this.token !== null,
    });

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized responses (token expired or invalid)
    // Skip for login endpoint to allow login attempts
    if (response.status === 401 && !isLoginRequest) {
      console.log("Received 401 Unauthorized, token may be expired");
      handleTokenExpiration();
      throw new Error("Your session has expired. Please log in again.");
    }

    // Debug logging for response
    const responseUrl = `${API_BASE}${endpoint}`;
    console.log(`API Response: ${response.status} ${response.statusText}`, {
      url: responseUrl,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      API_BASE: API_BASE,
      endpoint: endpoint,
      responseUrl: responseUrl,
    });

    if (response.status === 401) {
      // Token expired, clear and redirect
      this.clearToken();
      window.location.href = "/login";
      throw new Error("Authentication required");
    }

    if (response.status === 403) {
      // Forbidden - token might be invalid or user doesn't have permission
      const errorText = await response.text();
      let errorMessage = "Access forbidden";
      let errorDetails = {};

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
        errorDetails = errorJson;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      console.error(`API Error 403 (Forbidden):`, {
        message: errorMessage,
        endpoint: `${API_BASE}${endpoint}`,
        errorText,
        errorDetails,
        headers: Object.fromEntries(response.headers.entries()),
        tokenExists: this.token !== null,
        tokenLength: this.token?.length,
      });

      // Don't clear token on 403 - it might be a permission issue, not auth issue
      throw new Error(`Access forbidden: ${errorMessage}`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `API Error: ${response.status}`;

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      console.error(`API Error ${response.status}:`, errorMessage, {
        endpoint: `${API_BASE}${endpoint}`,
        errorText,
      });

      throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  // Authentication
  async requestOtp(email) {
    return this.request("/api/auth/request-otp", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async verifyOtp(email, code) {
    return this.request("/api/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });
  }

  async getProfile() {
    return this.request("/api/user/profile");
  }

  // User management (SUPER_ADMIN only)
  async getUsers() {
    return this.request("/api/users");
  }

  async createUser(email, name, role) {
    return this.request("/api/users", {
      method: "POST",
      body: JSON.stringify({ email, name, role }),
    });
  }

  async deleteUser(id) {
    return this.request(`/api/users/${id}`, { method: "DELETE" });
  }

  // Payroll Management
  async getPayrollRun(yearMonth) {
    try {
      return await this.request(`/api/payroll/${yearMonth}`);
    } catch (error) {
      if (error.message?.startsWith("API Error: 404")) return null;
      throw error;
    }
  }

  async getPayrollEligibility(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/eligibility`);
  }

  async getPayrollTutors(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/tutors`);
  }

  async getPayrollLineItems(yearMonth, canonicalTutorId = null, page = 0, size = 100) {
    const params = new URLSearchParams({ page, size });
    if (canonicalTutorId) params.append("canonicalTutorId", canonicalTutorId);
    return this.request(`/api/payroll/${yearMonth}/line-items?${params}`);
  }

  async getAllPayrollLineItems(yearMonth, canonicalTutorId = null) {
    const size = 100;
    let page = 0;
    let allContent = [];
    let isLast = false;
    while (!isLast) {
      const result = await this.getPayrollLineItems(yearMonth, canonicalTutorId, page, size);
      allContent = allContent.concat(result.content);
      isLast = result.last;
      page++;
    }
    return allContent;
  }

  async getLateReportExemptions(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/exemptions`);
  }

  async addLateReportExemption(tcAppointmentId, branchId) {
    return this.request(`/api/payroll/exemptions`, {
      method: "POST",
      body: JSON.stringify({ tcAppointmentId, branchId }),
    });
  }

  async removeLateReportExemption(tcAppointmentId, branchId) {
    return this.request(`/api/payroll/exemptions/${tcAppointmentId}/${branchId}`, {
      method: "DELETE",
    });
  }

  async getPayrollValidation(yearMonth) {
    try {
      return await this.request(`/api/payroll/${yearMonth}/validation`);
    } catch (error) {
      if (error.message?.startsWith("API Error: 404")) return null;
      throw error;
    }
  }

  async validatePayroll(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/validate`, { method: "POST" });
  }

  async generatePayroll(yearMonth, force = false) {
    return this.request(`/api/payroll/${yearMonth}/generate?force=${force}`, { method: "POST" });
  }

  async lockPayroll(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/lock`, { method: "POST" });
  }

  async markTutorPaid(yearMonth, canonicalTutorId, notes) {
    return this.request(`/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/pay`, {
      method: "POST",
      body: JSON.stringify({ notes: notes || null }),
    });
  }

  async unmarkTutorPaid(yearMonth, canonicalTutorId) {
    return this.request(`/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/pay`, { method: "DELETE" });
  }

  async markAllPaid(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/pay-all`, { method: "POST" });
  }

  async createAdjustment(yearMonth, canonicalTutorId, amount, description) {
    return this.request(`/api/payroll/${yearMonth}/adjustments`, {
      method: "POST",
      body: JSON.stringify({ canonicalTutorId, amount, description }),
    });
  }

  async updateAdjustment(yearMonth, lineItemId, amount, description) {
    return this.request(`/api/payroll/${yearMonth}/adjustments/${lineItemId}`, {
      method: "PUT",
      body: JSON.stringify({ amount, description }),
    });
  }

  async deleteAdjustment(yearMonth, lineItemId) {
    return this.request(`/api/payroll/${yearMonth}/adjustments/${lineItemId}`, {
      method: "DELETE",
    });
  }

  // Email Queue
  async queueTutorEmail(yearMonth, canonicalTutorId) {
    return this.request(`/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/email`, {
      method: "POST",
    });
  }

  async queueAllEmails(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/email-all`, { method: "POST" });
  }

  async getEmailStatus(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/email-status`);
  }

  // Wise Payments
  async getWiseBalance() {
    return this.request("/api/payroll/wise/balance");
  }

  async previewWisePayments(yearMonth, tutorIds) {
    return this.request(`/api/payroll/${yearMonth}/wise/preview`, {
      method: "POST",
      body: JSON.stringify({ tutorIds }),
    });
  }

  async initiateWisePayments(yearMonth, items) {
    return this.request(`/api/payroll/${yearMonth}/wise/initiate`, {
      method: "POST",
      body: JSON.stringify({ items }),
    });
  }

  // Sync Operations (Asynchronous - returns job ID)
  // Note: Tutor sync is now part of full sync. Use startFullSync for all sync operations.

  // Note: Individual sync endpoints have been removed. Use startFullSync for all sync operations.

  // Note: Payroll sync has been removed. Payroll generation is now synchronous.

  async startFullSync(yearMonth, params = {}) {
    const queryParams = new URLSearchParams({ yearMonth, ...params });
    return this.request(`/api/sync/full/manual?${queryParams}`, {
      method: "POST",
    });
  }

  // Job Status Tracking
  async getJobStatus(jobId) {
    return this.request(`/api/sync/jobs/${jobId}`);
  }

  async getActiveJobs() {
    return this.request("/api/sync/jobs/active");
  }

  async getLatestJobByType(jobType) {
    return this.request(`/api/sync/jobs/latest/${jobType}`);
  }

  async getJobsByType(jobType, limit = 10) {
    return this.request(`/api/sync/jobs/type/${jobType}?limit=${limit}`);
  }

  async getRecentJobs(limit = 10) {
    return this.request(`/api/sync/jobs/recent?limit=${limit}`);
  }

  // Sync Status (Historical)
  async getSyncStatus(yearMonth) {
    return this.request(`/api/sync/status/${yearMonth}`);
  }

  // Note: Payroll sync status endpoint has been removed as payroll sync is no longer available

  // Tutor Management
  async getTutors(page = 0, size = 50) {
    return this.request(`/api/tutors?page=${page}&size=${size}`);
  }

  async setTutorDormant(canonicalTutorId, dormant) {
    return this.request(`/api/tutors/canonical/${canonicalTutorId}/dormant`, {
      method: "PATCH",
      body: JSON.stringify({ dormant }),
    });
  }

  async getTutorById(tutorId) {
    return this.request(`/api/tutors/${tutorId}`);
  }

  // Tutor Rate Management
  async getTutorRateHistory(tutorId) {
    return this.request(`/api/tutor-rates/tutor/${tutorId}`);
  }

  async getTutorCurrentRate(tutorId) {
    return this.request(`/api/tutor-rates/tutor/${tutorId}/current`);
  }

  async getTutorRateForYearMonth(tutorId, yearMonth) {
    return this.request(`/api/tutor-rates/tutor/${tutorId}/rate/${yearMonth}`);
  }

  async getTutorRateHistorySummary(tutorId) {
    return this.request(`/api/tutor-rates/tutor/${tutorId}/summary`);
  }

  async createTutorRate(rateData) {
    return this.request("/api/tutor-rates", {
      method: "POST",
      body: JSON.stringify(rateData),
    });
  }

  async updateTutorRate(rateId, rateData) {
    return this.request(`/api/tutor-rates/${rateId}`, {
      method: "PUT",
      body: JSON.stringify(rateData),
    });
  }

  async deleteTutorRate(rateId) {
    return this.request(`/api/tutor-rates/${rateId}`, {
      method: "DELETE",
    });
  }

  // Tutor Payroll Settings
  async getTutorPayrollSettings(canonicalTutorId) {
    return this.request(`/api/tutor-payroll-settings/${canonicalTutorId}`);
  }

  async updateTutorPayrollSettings(canonicalTutorId, excludeFromBonuses) {
    return this.request(`/api/tutor-payroll-settings/${canonicalTutorId}`, {
      method: "PATCH",
      body: JSON.stringify({ excludeFromBonuses }),
    });
  }

  async updateTutorEmployeeStatus(canonicalTutorId, isEmployee, monthlyBasePay) {
    return this.request(`/api/tutor-payroll-settings/${canonicalTutorId}/employee`, {
      method: "PATCH",
      body: JSON.stringify({ isEmployee, monthlyBasePay }),
    });
  }

  // Mentor Assignment Management
  async getMentorAssignmentHistory(tutorId) {
    return this.request(`/api/mentor-assignments/tutor/${tutorId}`);
  }

  async getCurrentMentorAssignment(tutorId) {
    return this.request(`/api/mentor-assignments/tutor/${tutorId}/current`);
  }

  async getCurrentMenteesOfMentor(mentorId) {
    return this.request(
      `/api/mentor-assignments/mentor/${mentorId}/current-mentees`,
    );
  }

  async createMentorAssignment(data) {
    return this.request("/api/mentor-assignments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateMentorAssignment(assignmentId, data) {
    return this.request(`/api/mentor-assignments/${assignmentId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteMentorAssignment(assignmentId) {
    return this.request(`/api/mentor-assignments/${assignmentId}`, {
      method: "DELETE",
    });
  }

  // Student Management
  async getStudents(branchId) {
    const query = branchId ? `?branchId=${branchId}` : "";
    return this.request(`/api/students${query}`);
  }

  async searchStudents(name, branchId) {
    const params = new URLSearchParams({ name });
    if (branchId) params.append("branchId", branchId);
    return this.request(`/api/students/search?${params}`);
  }

  // Counselor Assignment Management
  async getCounselorAssignmentHistory(studentId) {
    return this.request(`/api/counselor-assignments/student/${studentId}`);
  }

  async getCurrentCounselorAssignment(studentId) {
    return this.request(
      `/api/counselor-assignments/student/${studentId}/current`,
    );
  }

  async createCounselorAssignment(data) {
    return this.request("/api/counselor-assignments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateCounselorAssignment(assignmentId, data) {
    return this.request(`/api/counselor-assignments/${assignmentId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteCounselorAssignment(assignmentId) {
    return this.request(`/api/counselor-assignments/${assignmentId}`, {
      method: "DELETE",
    });
  }

  // Demo Lesson Management
  async getDemoLessons(params = {}) {
    const query = new URLSearchParams();
    if (params.yearMonth) query.append("yearMonth", params.yearMonth);
    if (params.branchId) query.append("branchId", params.branchId);
    if (params.converted !== undefined && params.converted !== null)
      query.append("converted", params.converted);
    const qs = query.toString();
    return this.request(`/api/demo-lessons${qs ? "?" + qs : ""}`);
  }

  async markDemoLessonConverted(data) {
    return this.request("/api/demo-lessons/conversions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateDemoLessonConversion(conversionId, data) {
    return this.request(`/api/demo-lessons/conversions/${conversionId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteDemoLessonConversion(conversionId) {
    return this.request(`/api/demo-lessons/conversions/${conversionId}`, {
      method: "DELETE",
    });
  }

  async triggerAllTimeDemoSync() {
    return this.request("/api/demo-lessons/sync", { method: "POST" });
  }

  // Wise Integration
  async getWiseRecipients() {
    return this.request("/api/wise/recipients");
  }

  async triggerWiseSync() {
    return this.request("/api/wise/sync", { method: "POST" });
  }

  async getWiseMappings() {
    return this.request("/api/wise/mappings");
  }

  async getWiseMappingForTutor(canonicalTutorId) {
    return this.request(`/api/wise/mappings/${canonicalTutorId}`);
  }

  async setWiseMapping(canonicalTutorId, wiseAccountId) {
    return this.request(`/api/wise/mappings/${canonicalTutorId}`, {
      method: "PUT",
      body: JSON.stringify({ wiseAccountId }),
    });
  }

  async removeWiseMapping(canonicalTutorId) {
    return this.request(`/api/wise/mappings/${canonicalTutorId}`, {
      method: "DELETE",
    });
  }

  // Tutor notes (admin-only, shown in app but not in PDF)
  async getTutorNotes(yearMonth, canonicalTutorId) {
    return this.request(`/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/notes`);
  }

  async addTutorNote(yearMonth, canonicalTutorId, text) {
    return this.request(`/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/notes`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  }

  async deleteTutorNote(yearMonth, canonicalTutorId, noteId) {
    return this.request(`/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/notes/${noteId}`, {
      method: "DELETE",
    });
  }

  // PDF / ZIP Downloads
  async downloadPayrollZip(yearMonth) {
    if (!this.checkTokenExpiration()) throw new Error("Session expired");
    const response = await fetch(`${API_BASE}/api/payroll/${yearMonth}/download-zip`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (response.status === 404) throw new Error("No payroll found for " + yearMonth);
    if (!response.ok) throw new Error("Download failed: " + response.status);
    return response.blob();
  }

  async downloadTutorPdf(yearMonth, canonicalTutorId) {
    if (!this.checkTokenExpiration()) throw new Error("Session expired");
    const response = await fetch(
      `${API_BASE}/api/payroll/${yearMonth}/tutors/${canonicalTutorId}/pdf`,
      { headers: { Authorization: `Bearer ${this.token}` } }
    );
    if (response.status === 404) throw new Error("No payroll PDF found");
    if (!response.ok) throw new Error("Download failed: " + response.status);
    return response.blob();
  }

  // Email quota
  async getEmailQuota() {
    return this.request("/api/email-quota");
  }

  // Pre-generation custom line items (pending adjustments)
  async getPendingAdjustments(yearMonth) {
    return this.request(`/api/payroll/${yearMonth}/pending-adjustments`);
  }

  async createPendingAdjustment(yearMonth, canonicalTutorId, amount, description) {
    return this.request(`/api/payroll/${yearMonth}/pending-adjustments`, {
      method: "POST",
      body: JSON.stringify({ canonicalTutorId, amount, description }),
    });
  }

  async updatePendingAdjustment(yearMonth, id, amount, description) {
    return this.request(`/api/payroll/${yearMonth}/pending-adjustments/${id}`, {
      method: "PUT",
      body: JSON.stringify({ amount, description }),
    });
  }

  async deletePendingAdjustment(yearMonth, id) {
    return this.request(`/api/payroll/${yearMonth}/pending-adjustments/${id}`, {
      method: "DELETE",
    });
  }
}

// Create singleton instance
const apiService = new ApiService();
export default apiService;
